import React from 'react';
import {
  ChevronIcon,
  CHEVRON_BOX_STYLE,
  chevronSlotStyle,
} from './chromeIcons';
import { SectionNodesProgress } from './SectionNodesProgress';
import type { SectionProgressState } from './sectionProgress';

/**
 * Шапка квиза в режиме `chrome.progress: 'section-nodes'` — строка эталона
 * Slimkit (spec shared/header.md «Геометрия»): «назад» 44×24 | узлы по
 * разделам на всё оставшееся место | «вперёд» 44×24. Высота строки 24.
 *
 * Слоты кнопок держат место всегда (44 px), даже когда кнопки нет: иначе
 * узлы съезжали бы от центра на первом экране и там, где «вперёд» скрыта.
 * Вид кнопки «назад» в этом режиме — шеврон при любом `chrome.backButton`,
 * кроме 'none' (тогда — пустой слот).
 */
export interface QuizSectionHeaderProps {
  /** null — кнопки нет (первый экран или пак без «назад»): пустой слот. */
  onBack: (() => void) | null;
  backLabel: string;
  /** null — пак кнопку «вперёд» не рисует: пустой слот. */
  forward: { visible: boolean; onClick: () => void; label: string } | null;
  /** null — индикатор уступил блоку «Текст прогресса» (WEB-1713). */
  progress: SectionProgressState | null;
  onPressKeepFocus?: (e: React.MouseEvent) => void;
}

export function QuizSectionHeader({
  onBack,
  backLabel,
  forward,
  progress,
  onPressKeepFocus,
}: QuizSectionHeaderProps) {
  return (
    <div
      data-testid="quiz-screen-chrome"
      data-chrome="section-nodes"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        height: 24,
        marginBottom: 16,
      }}
    >
      {onBack ? (
        <button
          type="button"
          onClick={onBack}
          onMouseDown={onPressKeepFocus}
          aria-label={backLabel}
          data-testid="quiz-back-button"
          data-shape="chevron"
          style={chevronSlotStyle('back')}
        >
          <span style={CHEVRON_BOX_STYLE}>
            <ChevronIcon direction="back" />
          </span>
        </button>
      ) : (
        <span
          aria-hidden="true"
          data-testid="quiz-back-spacer"
          style={{ minWidth: 44, height: 24 }}
        />
      )}

      {progress ? (
        <SectionNodesProgress state={progress} />
      ) : (
        <span style={{ flex: 1 }} />
      )}

      {forward ? (
        <ForwardChevronButton
          {...forward}
          onPressKeepFocus={onPressKeepFocus}
        />
      ) : (
        <span aria-hidden="true" style={{ minWidth: 44, height: 24 }} />
      )}
    </div>
  );
}

/**
 * Кнопка «вперёд» (пак `chrome.forwardButton: 'chevron'`, header.md п.5):
 * зеркальный шеврон, невидима и не нажимается, пока на экране нет ответа;
 * появляется `opacity .2s ease-in-out`. Нажатие — то же, что «Далее».
 */
export function ForwardChevronButton({
  visible,
  onClick,
  label,
  onPressKeepFocus,
}: {
  visible: boolean;
  onClick: () => void;
  label: string;
  onPressKeepFocus?: (e: React.MouseEvent) => void;
}) {
  return (
    <button
      type="button"
      onClick={visible ? onClick : undefined}
      onMouseDown={onPressKeepFocus}
      aria-label={label}
      aria-hidden={visible ? undefined : true}
      tabIndex={visible ? undefined : -1}
      data-testid="quiz-forward-button"
      data-visible={visible ? 'true' : 'false'}
      style={{
        ...chevronSlotStyle('forward'),
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
        transition: 'opacity 0.2s ease-in-out',
      }}
    >
      <span style={CHEVRON_BOX_STYLE}>
        <ChevronIcon direction="forward" />
      </span>
    </button>
  );
}
